import React, { useState } from 'react';
import styled from '@emotion/styled';
import { FormikProps, withFormik } from 'formik';
import * as yup from 'yup';
import ConfirmationDialog from '../../../components/structure/ConfirmationDialog';
import Button from '../../../components/general/Button';
import MemberContainer from '../../../state/containers/MemberContainer';
import OrganizationContainer from '../../../state/containers/OrganizationContainer';
import SparkFormInvitationRequest from '../../../models/organizations/api/SparkFormInvitationRequest';
import { 
    color_shades_darkest, 
    color_colors_ocean, 
    silver_two, 
    dark_grey_blue, 
    color_shades_lightest 
} from '../../../constants/colors';

export interface InviteRecipient {
    email: string;
    firstName: string;
    lastName: string;
}

export interface InviteSparkFormData {
    invitations: InviteRecipient[];
    sendEmail?: boolean;
}

export interface GeneratedLinkItem {
    email: string;
    name: string;
    url: string;
}

export interface InviteSparkFormStatus {
    error?: string;
    generatedLinks?: GeneratedLinkItem[];
}

export interface InviteSparkFormModalProps {
    applicationId: string;
    appName?: string;
    title?: string;
    isShareFormOpen: boolean;
    setIsShareFormOpen: (bool: boolean) => void;
    memberContainer: MemberContainer;
    onInviteSent?: (applicationId: string, email: string | string[]) => void | Promise<void>;
}

const InnerForm: React.FC<InviteSparkFormModalProps & FormikProps<InviteSparkFormData>> = (props) => {
    const [copiedIndices, setCopiedIndices] = useState<Record<number, boolean>>({});
    const [isManualEmail, setIsManualEmail] = useState<boolean>(false);

    const isLoading = props.memberContainer.state.isLoading;

    const handleURLCopy = (text: string, index: number) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedIndices(prev => ({ ...prev, [index]: true }));
        setTimeout(() => {
            setCopiedIndices(prev => ({ ...prev, [index]: false }));
        }, 3000);
    };

    const handleCustomSubmit = (sendEmail: boolean) => {
        props.setFieldValue('sendEmail', sendEmail);
        props.submitForm();
    };

    if (!props.isShareFormOpen) {
        return null;
    }

    const modalTitle = props.title || (props.appName ? `Invite ${props.appName} Team to Spark` : 'Invite Voicify Dental Team to Spark');
    const inviteCount = props.values.invitations.length;
    const isMultiple = inviteCount > 1;
    const generatedLinks = (props.status as InviteSparkFormStatus | undefined)?.generatedLinks || [];

    return (
        <ConfirmationDialog
            title={modalTitle}
            onClose={() => props.setIsShareFormOpen(false)}
            size="large"
            contentScrollable={true}
            customFooter={(
                <PreviewEmailFooterContainer>
                    <PreviewEmailFooterLeft>
                        <Button
                            themes={["white", "secondary"]}
                            text="Cancel"
                            type="button"
                            disabled={props.isSubmitting || isLoading}
                            onClick={() => props.setIsShareFormOpen(false)}
                        />
                    </PreviewEmailFooterLeft>
                    <PreviewEmailFooterRight>
                        <PreviewEmailLabel>
                            <input
                                type="checkbox"
                                checked={isManualEmail}
                                onChange={(e) => {
                                    setIsManualEmail(e.target.checked);
                                    props.setFieldValue('sendEmail', !e.target.checked);
                                }}
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#0284c7'
                                }}
                            />
                            I'm sending the invite manually
                        </PreviewEmailLabel>
                        <Button
                            themes={["primary"]}
                            text={isManualEmail 
                                ? (isMultiple ? `Generate Links (${inviteCount})` : "Generate Link") 
                                : (isMultiple ? `Send Invites (${inviteCount})` : "Send Invite")
                            }
                            type="button"
                            loading={props.isSubmitting || isLoading}
                            disabled={props.isSubmitting || isLoading}
                            onClick={() => handleCustomSubmit(!isManualEmail)}
                        />
                    </PreviewEmailFooterRight>
                </PreviewEmailFooterContainer>
            )}
        >
            <div style={{ padding: '24px', fontFamily: 'Muli, sans-serif', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{
                    background: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: '#334155',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '1.4'
                }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="16" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <span>
                        Inviting others gives them full access to all resources and data within the organization. Please invite trusted individuals only.
                    </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {props.values.invitations.map((invitation, idx) => {
                        const invitationErrors = (props.errors.invitations?.[idx] as any) || {};
                        const invitationTouched = (props.touched.invitations?.[idx] as any) || {};

                        return (
                            <RecipientCard key={idx} isMultiple={isMultiple}>
                                {isMultiple && (
                                    <RecipientHeader>
                                        <RecipientTitle>Recipient {idx + 1}</RecipientTitle>
                                        <RemoveRecipientButton
                                            type="button"
                                            title="Remove recipient"
                                            onClick={() => {
                                                const updated = props.values.invitations.filter((_, i) => i !== idx);
                                                props.setFieldValue('invitations', updated);
                                            }}
                                            disabled={props.isSubmitting || isLoading}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                <line x1="10" y1="11" x2="10" y2="17" />
                                                <line x1="14" y1="11" x2="14" y2="17" />
                                            </svg>
                                            <span>Remove</span>
                                        </RemoveRecipientButton>
                                    </RecipientHeader>
                                )}

                                <ModalField>
                                    <ModalLabel>Recipient Email *</ModalLabel>
                                    <ModalInput
                                        name={`invitations[${idx}].email`}
                                        type="email"
                                        placeholder="user@company.com"
                                        value={invitation.email}
                                        onChange={props.handleChange}
                                        onBlur={props.handleBlur}
                                        disabled={props.isSubmitting || isLoading}
                                    />
                                    {invitationTouched.email && invitationErrors.email && (
                                        <FieldError>{invitationErrors.email}</FieldError>
                                    )}
                                </ModalField>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <ModalField>
                                        <ModalLabel>First Name *</ModalLabel>
                                        <ModalInput
                                            name={`invitations[${idx}].firstName`}
                                            type="text"
                                            placeholder="e.g. Jane"
                                            value={invitation.firstName}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            disabled={props.isSubmitting || isLoading}
                                        />
                                        {invitationTouched.firstName && invitationErrors.firstName && (
                                            <FieldError>{invitationErrors.firstName}</FieldError>
                                        )}
                                    </ModalField>

                                    <ModalField>
                                        <ModalLabel>Last Name *</ModalLabel>
                                        <ModalInput
                                            name={`invitations[${idx}].lastName`}
                                            type="text"
                                            placeholder="e.g. Doe"
                                            value={invitation.lastName}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            disabled={props.isSubmitting || isLoading}
                                        />
                                        {invitationTouched.lastName && invitationErrors.lastName && (
                                            <FieldError>{invitationErrors.lastName}</FieldError>
                                        )}
                                    </ModalField>
                                </div>
                            </RecipientCard>
                        );
                    })}

                    <AddRecipientButton
                        type="button"
                        onClick={() => {
                            props.setFieldValue('invitations', [
                                ...props.values.invitations,
                                { email: '', firstName: '', lastName: '' }
                            ]);
                        }}
                        disabled={props.isSubmitting || isLoading}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        <span>Add Another Recipient</span>
                    </AddRecipientButton>
                </div>

                <ModalField style={{ gap: '4px' }}>
                    <ModalLabel>From:</ModalLabel>
                    <span style={{ fontSize: '14px', color: `${color_shades_darkest}`, fontWeight: 500, paddingLeft: '2px' }}>
                        support@voicify.com
                    </span>
                </ModalField>

                {props?.status?.error && (
                    <div style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        padding: '12px 16px',
                        color: '#b91c1c',
                        fontSize: '14px',
                        fontWeight: 500,
                        whiteSpace: 'pre-line'
                    }}>
                        {props.status.error}
                    </div>
                )}

                {generatedLinks.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {generatedLinks.map((linkItem, idx) => (
                            <GeneratedLinkCard key={idx}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                        </svg>
                                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#0369a1' }}>
                                            Generated Invitation URL {isMultiple ? `(${linkItem.name || linkItem.email})` : ''}
                                        </span>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        background: '#ffffff',
                                        border: '1px solid #bae6fd',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        gap: '12px'
                                    }}>
                                        <span style={{
                                            fontFamily: 'monospace',
                                            fontSize: '13px',
                                            color: '#0f172a',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1
                                        }}>
                                            {linkItem.url}
                                        </span>
                                        <Button
                                            themes={['primary-small']}
                                            text={copiedIndices[idx] ? "✓ Copied!" : "Copy Link"}
                                            type="button"
                                            onClick={() => handleURLCopy(linkItem.url, idx)}
                                        />
                                    </div>
                                </div>
                            </GeneratedLinkCard>
                        ))}
                    </div>
                )}
            </div>
        </ConfirmationDialog>
    );
};

const InviteSparkFormModal = withFormik<InviteSparkFormModalProps, InviteSparkFormData>({
    mapPropsToValues: () => ({
        invitations: [
            { email: '', firstName: '', lastName: '' }
        ],
        sendEmail: true
    }),
    validationSchema: yup.object().shape({
        invitations: yup.array().of(
            yup.object().shape({
                firstName: yup.string().trim().required("You must enter a first name"),
                lastName: yup.string().trim().required("You must enter a last name"),
                email: yup.string().trim().email("Must be a valid email").required("You must enter an email address"),
            })
        ).min(1, "Please add at least one invitation")
    }),
    handleSubmit: async (values, { props, setStatus, setSubmitting, setFieldValue }) => {
        const currentOrgId = JSON.parse(localStorage.getItem('CURRENT_ORG') || '{}')?.id;
        if (!currentOrgId) {
            setSubmitting(false);
            setStatus({ error: "Could not find organization. Please refresh." });
            return;
        }

        const organizationContainer = new OrganizationContainer();
        const systemRolesResults = await organizationContainer.getSystemRoles();
        if (systemRolesResults.resultType !== "Ok") {
            setSubmitting(false);
            setStatus({ error: systemRolesResults.errors[0] || "Failed to load roles." });
            return;
        }

        const adminRole = systemRolesResults.data.find(role => role.name === "Admin");
        setStatus(undefined);

        const errors: string[] = [];
        const successfulEmails: string[] = [];
        const generatedLinks: GeneratedLinkItem[] = [];

        for (const invite of values.invitations) {
            const request: SparkFormInvitationRequest = {
                applicationId: props.applicationId,
                email: invite.email.trim(),
                firstName: invite.firstName.trim(),
                lastName: invite.lastName.trim(),
                isAdmin: true,
                isAddedToApplications: true,
                applicationRoleId: adminRole ? adminRole.id : '',
                sendEmail: values.sendEmail !== false
            };

            const result = await props.memberContainer.inviteSparkForm(currentOrgId, request);
            if (result.resultType !== "Ok") {
                const errMsg = result.errors?.[0] || 'Failed to send invite';
                errors.push(`${invite.email}: ${errMsg}`);
            } else {
                successfulEmails.push(invite.email.trim());
                if (result.data?.inviteURL) {
                    generatedLinks.push({
                        email: invite.email.trim(),
                        name: `${invite.firstName} ${invite.lastName}`.trim(),
                        url: result.data.inviteURL
                    });
                }
            }
        }

        setSubmitting(false);

        if (successfulEmails.length > 0) {
            await props.onInviteSent?.(props.applicationId, successfulEmails);
        }

        if (errors.length > 0) {
            setStatus({
                error: errors.join('\n'),
                generatedLinks: generatedLinks.length > 0 ? generatedLinks : undefined
            });
            if (successfulEmails.length > 0) {
                const remaining = values.invitations.filter(inv => !successfulEmails.includes(inv.email.trim()));
                if (remaining.length > 0) {
                    setFieldValue('invitations', remaining);
                }
            }
        } else {
            if (values.sendEmail !== false) {
                props.setIsShareFormOpen(false);
            } else {
                setStatus({ generatedLinks });
            }
        }
    }
})(InnerForm);

const ModalField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    font-family: Muli, sans-serif;
`;

const ModalLabel = styled.label`
    font-family: Muli, sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: ${dark_grey_blue};
`;

const ModalInput = styled.input`
    height: 40px;
    border-radius: 6px;
    border: 1px solid ${silver_two};
    background: ${color_shades_lightest};
    color: ${color_shades_darkest};
    padding: 0 12px;
    font-size: 14px;
    font-family: Muli, sans-serif;
    width: 100%;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 2px rgba(0, 125, 187, 0.12);
    }

    &:disabled {
        background: #f1f5f9;
        color: #94a3b8;
        cursor: not-allowed;
    }
`;

const FieldError = styled.span`
    color: #dc2626;
    font-size: 12px;
    font-weight: 600;
`;

const GeneratedLinkCard = styled.div`
    background: #f0f9ff;
    border: 1px solid #b9e6fe;
    border-radius: 8px;
    padding: 16px;
    margin-top: 4px;
`;

const RecipientCard = styled.div<{ isMultiple: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: ${props => props.isMultiple ? '16px' : '0'};
    background: ${props => props.isMultiple ? '#f8fafc' : 'transparent'};
    border: ${props => props.isMultiple ? '1px solid #e2e8f0' : 'none'};
    border-radius: ${props => props.isMultiple ? '8px' : '0'};
`;

const RecipientHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 8px;
    border-bottom: 1px solid #e2e8f0;
`;

const RecipientTitle = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    font-family: Muli, sans-serif;
`;

const RemoveRecipientButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: transparent;
    border: none;
    color: #ef4444;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    font-family: Muli, sans-serif;

    &:hover:not(:disabled) {
        background: #fef2f2;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const AddRecipientButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #f0f9ff;
    border: 1px dashed #0284c7;
    border-radius: 6px;
    padding: 10px 16px;
    color: #0284c7;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    align-self: flex-start;
    transition: all 0.2s ease;
    font-family: Muli, sans-serif;

    &:hover:not(:disabled) {
        background: #e0f2fe;
        border-color: #0369a1;
        color: #0369a1;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const PreviewEmailFooterContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 24px;
    box-sizing: border-box;
    font-family: Muli, sans-serif;

    @media (max-width: 640px) {
        flex-direction: column-reverse;
        gap: 12px;
        padding: 0 12px;
        align-items: stretch;
    }
`;

const PreviewEmailFooterLeft = styled.div`
    display: flex;
    gap: 12px;

    @media (max-width: 640px) {
        justify-content: space-between;
    }
`;

const PreviewEmailFooterRight = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;

    @media (max-width: 640px) {
        flex-direction: column-reverse;
        align-items: stretch;
        gap: 12px;
    }
`;

const PreviewEmailLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #334155;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
`;

export default InviteSparkFormModal;
